/**
 * LayoutBase Component
 *
 * A generic layout component for NioGEMS applications using Calcite UI components.
 *
 * Props:
 * - menuConfig (object):
 *     The configuration object for menu items. Each key represents a menu item
 *     and its value describes the item's properties.
 *
 * - navClass (string):
 *     CSS class name(s) applied to the Calcite navigation header.
 *
 * Features:
 * - Renders a Calcite shell with navigation, logo, and menu items.
 * - Supports dropdown and task-bar menu items.
 * - Handles common and app-specific task bars.
 * - Passes map-view context to child routes through Outlet.
 */

import {
  useReducer,
  useCallback,
  useState,
  useEffect
} from 'react';

import {
  Outlet,
  useLocation
} from 'react-router-dom';

import PropTypes from 'prop-types';

// Native Calcite web component registrations
import "@esri/calcite-components/components/calcite-shell";
import "@esri/calcite-components/components/calcite-shell-panel";
import "@esri/calcite-components/components/calcite-panel";
import "@esri/calcite-components/components/calcite-navigation";
import "@esri/calcite-components/components/calcite-navigation-logo";
import "@esri/calcite-components/components/calcite-menu-item";
import "@esri/calcite-components/components/calcite-menu";
import "@esri/calcite-components/components/calcite-action";

import MapTaskBar from '@cores/layouts/MapTaskBar';
import { AppResource } from '@config/config';
import { useLayoutUtils } from '@cores/layouts/utils/useLayoutUtils';
import { renderMenuItem } from '@cores/layouts/utils/menuRenderUtils';

import './layout.css';
import './LayoutBase.css';

function layoutReducer(state, action) {
  switch (action.type) {
    case 'NAVIGATE': {
      const { menuItem } = action;

      return {
        ...state,
        hasMapTaskBar:
          menuItem.hasMapTaskBar === true,
        appTaskBar:
          menuItem.taskItems || [],
        dropdownTaskBar: []
      };
    }

    case 'SET_DROPDOWN_TASKBAR': {
      return {
        ...state,
        dropdownTaskBar:
          action.dropdownTaskBar || []
      };
    }

    case 'RESET': {
      return {
        ...state,
        hasMapTaskBar: false,
        appTaskBar: [],
        dropdownTaskBar: []
      };
    }

    default:
      return state;
  }
}

// CSS uses 769px as the minimum desktop width.
const MOBILE_BREAKPOINT = 769;

export const LayoutBase = ({
  menuConfig,
  navClass
}) => {
  const {
    shouldShowMenuItem,
    handleMenuItemClick,
    handleTaskBarItemClick,
    handleDropdownTaskBarItemClick,
    handleMapTaskbarItemClick
  } = useLayoutUtils();

  const [mapView, setMapView] =
    useState(null);

  const [isPanelHidden, setIsPanelHidden] =
    useState(true);

  useEffect(() => {
    const handleResize = () => {
      if (
        window.innerWidth >=
          MOBILE_BREAKPOINT &&
        !isPanelHidden
      ) {
        setIsPanelHidden(true);
      }
    };

    window.addEventListener(
      'resize',
      handleResize
    );

    return () => {
      window.removeEventListener(
        'resize',
        handleResize
      );
    };
  }, [isPanelHidden]);

  const location = useLocation();
  const basePath = AppResource.basePath;

  const currentPath =
    location.pathname.replace(
      basePath,
      ''
    ) || '/';

  const currentMenuItem =
    Object.entries(menuConfig).find(
      ([, item]) =>
        currentPath === item.path
    );

  const initialMenuItem =
    currentMenuItem
      ? currentMenuItem[1]
      : null;

  const [layoutState, dispatch] =
    useReducer(layoutReducer, {
      hasMapTaskBar:
        initialMenuItem?.hasMapTaskBar ===
        true,

      appTaskBar:
        initialMenuItem?.taskItems || [],

      dropdownTaskBar: []
    });

  const handleMenuItemClickWithState =
    useCallback(
      (
        action,
        isNavigation,
        dropdownItemConfig,
        parentKey
      ) => {
        const menuItem =
          dropdownItemConfig
            ? menuConfig[parentKey]
            : menuConfig[action];

        if (!menuItem) {
          dispatch({
            type: 'RESET'
          });

          return;
        }

        if (
          dropdownItemConfig &&
          dropdownItemConfig
            .dropdownItemTaskItems
        ) {
          dispatch({
            type:
              'SET_DROPDOWN_TASKBAR',

            dropdownTaskBar:
              dropdownItemConfig
                .dropdownItemTaskItems
          });
        } else if (isNavigation) {
          dispatch({
            type:
              'SET_DROPDOWN_TASKBAR',

            dropdownTaskBar: []
          });
        }

        handleMenuItemClick(
          action,
          isNavigation,
          menuConfig,
          dispatch,
          dropdownItemConfig,
          parentKey
        );

        setIsPanelHidden(true);
      },
      [
        handleMenuItemClick,
        menuConfig
      ]
    );

  const handleMapTaskbarItemClickWithContext =
    useCallback(
      (action) => {
        handleMapTaskbarItemClick(
          action,
          mapView
        );
      },
      [
        handleMapTaskbarItemClick,
        mapView
      ]
    );

  const setMapviewCtxt = useCallback(
    (view) => {
      setMapView(view);
    },
    []
  );

  const handleMenuToggle = () => {
    setIsPanelHidden(
      (currentValue) =>
        !currentValue
    );
  };

  const setDropdownTaskBar = useCallback(
    (dropdownTaskBar) => {
      dispatch({
        type: 'SET_DROPDOWN_TASKBAR',
        dropdownTaskBar
      });
    },
    []
  );

  return (
    <calcite-shell>
      <calcite-navigation
        className={navClass}
        slot="header"
        aria-label="Main navigation"
      >
        <calcite-navigation-logo
          slot="logo"
          heading="NIOGEMS"
          thumbnail={AppResource.logo}
          className="public-logo"
          href={`${basePath}/`}
        />

        {/* Hamburger button for mobile */}
        <calcite-action
          className="hamburger-action"
          slot="content-end"
          icon="hamburger"
          text="Menu"
          onClick={handleMenuToggle}
        />

        <calcite-menu
          slot="content-end"
          className="main-menu"
        >
          {Object.entries(menuConfig).map(
            ([key, config]) =>
              shouldShowMenuItem(config) &&
              renderMenuItem(
                key,
                config,
                handleMenuItemClickWithState,
                setDropdownTaskBar,
                currentPath
              )
          )}
        </calcite-menu>

        {layoutState.hasMapTaskBar && (
          <calcite-navigation
            slot="navigation-secondary"
            aria-label="Common tasks navigation"
          >
            <MapTaskBar
              onMapTaskbarItemClick={
                handleMapTaskbarItemClickWithContext
              }
            />
          </calcite-navigation>
        )}

        {layoutState.appTaskBar.length >
          0 && (
          <calcite-navigation
            slot="navigation-secondary"
            aria-label="App task bar navigation"
          >
            <calcite-menu
              layout="horizontal"
              slot="content-start"
              expanded
            >
              {layoutState.appTaskBar.map(
                (item, index) => (
                  <calcite-menu-item
                    key={`${item.action}-${index}`}
                    text={item.text}
                    icon-start={item.icon}
                    aria-label={item.text}
                    onClick={() => {
                      handleTaskBarItemClick(
                        item.action,
                        layoutState.appTaskBar
                      );
                    }}
                  />
                )
              )}
            </calcite-menu>
          </calcite-navigation>
        )}

        {layoutState.dropdownTaskBar
          .length > 0 && (
          <calcite-navigation
            slot="navigation-tertiary"
            aria-label="Dropdown task bar navigation"
          >
            <calcite-menu
              layout="horizontal"
              slot="content-start"
              expanded
            >
              {layoutState.dropdownTaskBar.map(
                (item, index) => (
                  <calcite-menu-item
                    key={`${item.action}-${index}`}
                    text={item.text}
                    icon-start={item.icon}
                    aria-label={item.text}
                    onClick={() => {
                      handleDropdownTaskBarItemClick(
                        item.action,
                        layoutState.dropdownTaskBar
                      );
                    }}
                  />
                )
              )}
            </calcite-menu>
          </calcite-navigation>
        )}
      </calcite-navigation>

      {/* Side panel toggled by the mobile hamburger */}
      <calcite-shell-panel
        slot="panel-start"
        hidden={isPanelHidden}
      >
        <calcite-panel heading="Menu">
          <calcite-menu layout="vertical">
            {Object.entries(menuConfig).map(
              ([key, config]) =>
                shouldShowMenuItem(
                  config
                ) &&
                renderMenuItem(
                  key,
                  config,
                  handleMenuItemClickWithState,
                  setDropdownTaskBar,
                  currentPath
                )
            )}
          </calcite-menu>
        </calcite-panel>
      </calcite-shell-panel>

      <Outlet
        context={{
          setMapviewCtxt
        }}
      />
    </calcite-shell>
  );
};

LayoutBase.propTypes = {
  menuConfig:
    PropTypes.object.isRequired,

  navClass:
    PropTypes.string.isRequired,
};

export default LayoutBase;